import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";

import type { Transport } from "@tdelivr/api-client";
import { services } from "@tdelivr/api-client";
// The row shape API-02 publishes, imported rather than restated.
import type { PartnerCredentialSummary } from "@tdelivr/api-client/generated/partner-api/partner-credential.js";

import { boundedNote, PER_FETCH, type BoardRow } from "../board.ts";
import { credentialRow, CREDENTIAL_STATES, filterCredentials } from "../integrations.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import { RecordsBoard } from "./RecordsBoard.tsx";

/**
 * Integrations and webhooks: the keys this tenant's own systems call the platform with.
 *
 * Built to `17 WP16 Tenant Admin.dc.html`, page `integrations`, against API-02
 * `GET /v1/partner-credentials`.
 *
 * **This is the page where the projection is the security boundary rather than a narrowing.** API-02
 * holds live partner credentials, and ADR 0044 publishes the last four characters of a client
 * identifier, a display name, a state and three instants. It withholds the client identifier
 * itself, the argon2id hash of the secret, the key-store reference, the two approval references and
 * the granted scopes — and the truncation happens in SQL, so the full identifier is not read out of
 * PostgreSQL for this operation at all. The introspection operation, which takes a plaintext secret
 * and answers whether it is live, is not published: that is an authentication endpoint, and a
 * browser origin is not where one belongs.
 *
 * **`···9F2A` is the client identifier's tail and not the secret's.** The design's own closing note
 * says a secret is kept as its last four characters; API-02 keeps it as an argon2id hash, from which
 * no four characters can be recovered. Q-150, and the cell writes `client` rather than `key` so it
 * does not claim to be something it is not.
 *
 * **The `Webhook` column is this page's one absence, and it is a service boundary.** Its values are
 * API-03's delivery outcomes; that service publishes only internal operations, is not an owner of
 * this page, and what it publishes is its own decision to take, exactly as ADR 0044 was API-02's.
 * Drawn as the design's `—` with the reason on it. Q-153.
 *
 * **No write requirement.** The design's form is `Create API key or webhook → Create`, over a name,
 * a type, a scope set, an expiry, an endpoint URL and an event set. API-02's issue command requires
 * a `kmsKeyReference` matching `kms://…` and **two** distinct approval references matching
 * `urn:tdelivr:approval:…`; nothing on this platform mints either, the design's form has neither
 * field, and the command is not published under `/v1`. Half the form — the endpoint URL and the
 * events — belongs to API-03 and has no operation at all. Q-153.
 */

const credentialsApi = services.partner_api__partner_credential;

export interface IntegrationsProps {
  readonly transport: Transport;
  /** Innermost last: tenant, group, page. Rendered in the page body, never in the top bar. */
  readonly crumbs?: readonly string[];
  /** Injected so the expiry rule can be exercised without waiting for a date to pass. */
  readonly now?: () => number;
}

interface Loaded {
  readonly credentials: readonly PartnerCredentialSummary[];
  readonly truncated: boolean;
}

export function Integrations({ transport, crumbs, now }: IntegrationsProps): ReactNode {
  const [loaded, setLoaded] = useState<Loaded | undefined>(undefined);
  const [failure, setFailure] = useState<ViewState | undefined>(undefined);
  const [state, setState] = useState("");

  const load = useCallback(async () => {
    const client = credentialsApi.createPartnerCredentialClient(transport);
    try {
      const answered = await client.listTenantPartnerCredentialSummaries(PER_FETCH, undefined);
      setLoaded({
        credentials: answered.credentials,
        truncated: typeof answered.nextAfter === "string" && answered.nextAfter.length > 0,
      });
      setFailure(undefined);
    } catch (error) {
      setFailure(stateForError(error, ["partner-credentials:summary:read"]));
      setLoaded(undefined);
    }
  }, [transport]);

  useEffect(() => {
    void load();
  }, [load]);

  const instant = now === undefined ? Date.now() : now();

  const rows: readonly BoardRow[] | undefined = useMemo(() => {
    if (loaded === undefined) return undefined;
    return filterCredentials(loaded.credentials, state, instant).map((credential) =>
      credentialRow(credential, instant),
    );
  }, [loaded, state, instant]);

  const bounded = boundedNote(
    loaded?.credentials.length,
    loaded?.truncated ?? false,
    "credentials",
    "ordered by partner and credential",
  );

  return (
    <RecordsBoard
      crumbs={crumbs ?? []}
      title="Integrations and webhooks"
      /*
       * The design's chip reads `API-02 · API-03` and this one reads `API-02` alone, because the
       * webhook half of the page has no reachable operation and a chip naming a service this board
       * does not read would be the citation fault Q-149 records next door.
       */
      source="API-02"
      description="Keys for your own systems and the webhooks we send them."
      columns={["Integration", "Credential", "Webhook"]}
      rows={rows}
      {...(failure === undefined ? {} : { failure })}
      onRetry={() => {
        void load();
      }}
      purpose="Keys appear here as soon as the platform issues one for a partner of this tenant."
      primary={{
        label: "Create API key",
        refusal:
          "API-02's issue command requires a kms:// key reference and two distinct urn:tdelivr:approval references, neither of which anything on this platform mints and neither of which the design's form has a field for. The command is also not published under /v1: ADR 0044 publishes the read and nothing else, because issuing a credential answers a plaintext secret. The form's webhook half belongs to API-03, which publishes no reachable operation. Q-153.",
      }}
      filter={{
        unfiltered: "All states",
        options: CREDENTIAL_STATES.map((value) => ({ value, label: value })),
        value: state,
        onChange: setState,
      }}
      note={{
        /*
         * The design's `lock` note is replaced and keeps its kind, its glyph and its place.
         *
         * It reads "A secret is shown once when created, then only its last four characters."
         * The first half is exactly right — API-02 returns the plaintext once, with
         * `secretReturnedOnce` beside it, and the table's own `CHECK` refuses a stored document
         * containing a `plaintextSecret`. The second half is not: what is kept is an argon2id hash,
         * and four characters cannot be read back out of one. Q-150.
         */
        kind: "lock",
        glyph: "lock",
        text: "A secret is shown once when it is created and then kept only as a hash, so no part of it can be read back. The four characters below identify the credential's client, not its secret.",
      }}
      {...(bounded === undefined ? {} : { bounded })}
      footnote="Every credential this tenant holds, across all of its partners, however long ago it expired. A credential is usable only while it is active, inside its expiry, and carrying the scope being asked for."
      exportName="integrations"
      detailSubtitle="Integrations and webhooks · API-02"
      detailNote="A secret is shown once when it is created and then kept only as a hash, so no part of it can be read back. The four characters below identify the credential's client, not its secret."
    />
  );
}
